import React from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen, faScissors, faExclamationTriangle, faChartLine, faBalanceScale, faRulerCombined, faArrowUp, faCogs, faPercent, faCode, faProjectDiagram, faLightbulb, faExplosion, faBook } from '@fortawesome/free-solid-svg-icons';
import { faNodeJs, faAngular } from '@fortawesome/free-brands-svg-icons';

const blogs = [
    {
        title: "Node.js: The Backbone of Scalable Web Applications in 2025",
        description: "Discover why Node.js remains a top choice for building scalable and efficient web applications in 2025.",
        icon: faNodeJs,
        link: "/blogs/node-is-backbone",
        datePublished: "2025-01-01",
    },
    {
        title: "What is Deckle Matching? A Complete Guide for Paper Manufacturers",
        description: "Learn everything about deckle matching and its importance in paper manufacturing.",
        icon: faBookOpen,
        link: "/blogs/deckle-matching-guide",
        datePublished: "2025-06-01",
    },
    {
        title: "Modernizing Legacy Applications with .NET Core: A Step-by-Step Guide",
        description: "Learn how to successfully modernize your legacy applications using .NET Core with this comprehensive step-by-step guide.",
        icon: faCogs,
        link: "/blogs/modernizing-legacy-with-dotnet-core",
        datePublished: "2025-05-20",
    },
    {
        title: "How to Reduce Trim Waste in Paper Roll Cutting Processes",
        description: "Effective strategies to minimize trim waste and boost your mill’s profitability.",
        icon: faScissors,
        link: "/blogs/reduce-trim-waste",
        datePublished: "2025-06-10",
    },
    {
        title: "Why Angular Is Still the Most Reliable Framework for Enterprise Web Applications in 2025",
        description: "Angular offers stability, scalability, and maintainability for large, mission-critical enterprise web applications. See why it's trusted by Fortune 500 companies.",
        icon: faAngular,
        link: "/blogs/angular-most-reliable-framework",
        datePublished: "2025-07-15",
    },
    {
        title: "Top 5 Mistakes in Reel Slitting Operations and How to Avoid Them",
        description: "Avoid common errors in reel slitting and improve your production efficiency.",
        icon: faExclamationTriangle,
        link: "/blogs/mistakes-in-reel-slitting",
        datePublished: "2025-06-20",
    },
    {
        title: "Why Deckle Matching Optimization Can Save Lakhs in Production Costs",
        description: "Discover how optimizing deckle matching can lead to significant cost savings.",
        icon: faChartLine,
        link: "/blogs/deckle-matching-optimization",
        datePublished: "2025-07-01",
    },
    {
        title: "Manual vs. Software-Based Deckle Matching: A Cost-Benefit Analysis",
        description: "Compare manual and software-based deckle matching for better decision making.",
        icon: faBalanceScale,
        link: "/blogs/manual-vs-software-deckle-matching",
        datePublished: "2025-07-10",
    },
    {
        title: "Understanding GSM, Deckle Size, and Knife Limits in Paper Roll Planning",
        description: "A guide to GSM, deckle size, and knife limits for efficient paper roll planning.",
        icon: faRulerCombined,
        link: "/blogs/gsm-deckle-knife-limits",
        datePublished: "2025-07-20",
    },
    {
        title: "How DecklePro Improves Yield in Paper Slitting Machines",
        description: "See how DecklePro helps maximize yield and minimize waste in slitting operations.",
        icon: faArrowUp,
        link: "/blogs/decklepro-improves-yield",
        datePublished: "2025-08-01",
    },
    {
        title: "Deckle Matching Software: Must-Have Features for Modern Paper Mills",
        description: "Explore the essential features of deckle matching software for paper mills.",
        icon: faCogs,
        link: "/blogs/deckle-matching-software-features",
        datePublished: "2025-08-05",
    },
    {
        title: "How a Paper Mill Reduced 18% Waste Using DecklePro",
        description: "DecklePro, paper mill optimization software, deckle matching, kraft paper mill, reduce paper waste, production efficiency.",
        icon: faPercent,
        link: "/blogs/how-reduced-18-percent-waste",
        datePublished: "2025-10-10",
    },
    {
        title: ".NET 10 Development Services",
        description: "Transform your business with .NET 10 - faster, smarter, and AI-ready. Partner with Anmatics for modern .NET solutions.",
        icon: faCode,
        link: "/blogs/dotnet-10-business-solutions",
        datePublished: "2025-11-15",
    },
    {
        title: "Architecting Angular Applications for Scale: A Guide for Modern Development",
        description: "Learn practical strategies for architecting scalable Angular applications. Discover best practices for modular design, state management, lazy loading, and more.",
        icon: faProjectDiagram,
        link: "/blogs/architecting-angular-applications",
        datePublished: "2025-12-01",
    },
    {
        title: "Why most SaaS applications fail and how to avoid it",
        description: "Explore the common reasons SaaS applications fail and learn practical strategies to ensure your SaaS product's success in a competitive market.",
        icon: faLightbulb,
        link: "/blogs/why-saas-products-slow-down",
        datePublished: "2025-12-10",
    },
    {
        title: "7 Signs your Company Needs an Engineering Partner (Not More Employees)",
        description: "Discover the key signs your company needs a specialized engineering partner instead of hiring more developers. Learn how Anmatics accelerates product delivery and modernizes systems.",
        icon: faExplosion,
        link: "/blogs/engineering-partner-vs-hiring",
        datePublished: "2025-05-28",
    },
    {
        title: "A Founder’s Guide to Building Reliable Software Without a Full In-House Team",
        description: "Learn how founders and SaaS startups can build reliable, scalable software without hiring a full in-house engineering team. Discover the benefits of engineering partners, modular architecture, QA, CI/CD, and more from Anmatics Technologies.",
        icon: faBook,
        link: "/blogs/founders-guide-reliable-software",
        datePublished: "2025-01-15",
    }
];

const gradients = [
    "#2563EB",
    "#14B8A6",
    "#7B2CBF",
    "#F97316",
    "#F43F5E"
];

// Official colors for Node.js and Angular icons
const officialIconBg = {
    "Node.js: The Backbone of Scalable Web Applications in 2025": "#14B8A6",
    "Why Angular Is Still the Most Reliable Framework for Enterprise Web Applications in 2025": "#F43F5E",
};

const Blogs = () => (
    <section id="blogs" className="section blog-section position-relative overflow-hidden">
        <div className="blogs-overlay"></div>
        <Helmet>
            <title>Anmatics Insights | SaaS, AI, Modernization & DecklePro Articles</title>
            <meta name="description" content="Read Anmatics insights on SaaS engineering, AI workflow automation, enterprise modernization, Angular, Node.js, .NET, and DecklePro optimization for paper mills." />
            <link rel="canonical" href="https://www.anmatics.com/blogs" />

            {/* Open Graph / Social meta */}
            <meta property="og:title" content="Anmatics Insights | SaaS, AI, Modernization & DecklePro Articles" />
            <meta property="og:description" content="Practical articles from Anmatics on software engineering, SaaS scaling, AI workflows, modernization, and DecklePro optimization for paper mills." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://www.anmatics.com/blogs" />
            <meta property="og:image" content="https://www.anmatics.com/anmatics_logo_square_512.png" />
            <meta property="og:image:alt" content="Anmatics logo" />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Anmatics Insights | SaaS, AI, Modernization & DecklePro Articles" />
            <meta name="twitter:description" content="Practical articles from Anmatics on software engineering, SaaS scaling, AI workflows, modernization, and DecklePro optimization for paper mills." />
            <meta name="twitter:image" content="https://www.anmatics.com/anmatics_logo_square_512.png" />
            <meta name="twitter:image:alt" content="Anmatics logo" />

            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Blog",
                    "name": "Anmatics Blog",
                    "url": "https://www.anmatics.com/blogs",
                    "description": "Practical articles from Anmatics on software engineering, SaaS scaling, AI workflows, modernization, and DecklePro optimization for paper mills.",
                    "blogPost": blogs.map(blog => ({
                        "@type": "BlogPosting",
                        "headline": blog.title,
                        "description": blog.description,
                        "image": "https://www.anmatics.com/anmatics_logo_square_512.png",
                        "author": {
                            "@type": "Organization",
                            "name": "Anmatics Technologies"
                        },
                        "publisher": {
                            "@type": "Organization",
                            "name": "Anmatics",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://www.anmatics.com/anmatics_logo_square_512.png"
                            }
                        },
                        "datePublished": blog.datePublished,
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": `https://www.anmatics.com${blog.link}`
                        }
                    }))
                })}
            </script>
        </Helmet>
        <div className="container" data-aos="fade-up">
            <div className="section-header mb-4 text-center">
                <h3 className="contact-title gradient-title">Insights</h3>
            </div>
            <div className="row">
                {blogs.map((blog, idx) => {
                    // Use official color for Node.js and Angular, else fallback to gradient
                    const bgColor = officialIconBg[blog.title] || gradients[idx % gradients.length];
                    const iconColor =
                        blog.icon === faNodeJs ? "#fff" :
                            blog.icon === faAngular ? "#fff" :
                                undefined;
                    return (
                        <article className="col-lg-4 col-md-6 mb-4" key={idx} itemScope itemType="https://schema.org/BlogPosting">
                            <div className="card h-100 blog-card">
                                <div className="card-body d-flex flex-column align-items-center blog-card-body">
                                    <div
                                        className="blog-icon-wrapper"
                                        style={{
                                            background: bgColor,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={blog.icon}
                                            size="2x"
                                            className="blog-icon colorful-icon"
                                            aria-hidden="true"
                                            style={iconColor ? { color: iconColor } : {}}
                                        />
                                    </div>
                                    <h5 className="card-title fw-semibold text-center futuristic-text" itemProp="headline">{blog.title}</h5>
                                    <p className="card-text text-center futuristic-subtext" itemProp="description">{blog.description}</p>
                                    <meta itemProp="author" content="Anmatics Technologies" />
                                    <meta itemProp="datePublished" content={blog.datePublished} />
                                    <a href={blog.link} className="btn btn-gradient mt-auto rounded-pill shadow-sm" itemProp="url">
                                        Read More
                                    </a>
                                </div>
                            </div>
                        </article>
                    );
                })}
            </div>
            <p className="text-center mt-5 fs-6">
                Need a structured way to reduce trim waste and automate deckle planning?{" "}
                <a href="/product-details" className="text-link-gradient">
                    Explore DecklePro
                </a>.
            </p>
        </div>
    </section>
);

export default Blogs;
